import React, { useMemo, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { Editor } from '@tinymce/tinymce-react';
import { debounce } from 'lodash';

import usePrefix from 'utils/usePrefix';
import { noticeTypes, notificationTypes } from 'utils/constants';
import { useApp } from 'App';
import { getApiUrl } from 'utils/api';
import { noticeAttachmentApi } from 'utils/api/attachments';
import { ActionTypes } from 'App/types';

import Button from 'components/Button';
import DetailsTitle from 'components/DetailsTitle';
import FlexDiv from 'components/FlexDiv';
import AttachmentsList from 'components/Attachments';
import LanguageSelect from 'components/LanguageSelect';
import NoticeTypeSelect from 'components/NoticeTypeSelect';
import ToggleSwitch from 'components/ToggleSwitch';
import FormField from 'components/FormField';
import { FormGroupStyled } from 'components/Forms/styles';
import InputField from 'components/InputField';
import Rating from 'components/Rating';
import ConfirmModal from 'components/ConfirmModal';

import FileUpload from './FileUpload';
import {
  Content,
  FieldsContainer,
  EditorWrapper,
  EditorMessage,
  FieldTitle,
  Label,
  NoticeToolbarStyled,
  NoticeActionsStyled,
} from './styles';
import { FormValues } from '.';

const url = getApiUrl('/notices/type');
const urlImages = getApiUrl('/notices/images');

const EDITOR_DEBOUNCE_TIME = 300;

interface Props {
  onClose: () => void;
  noticeId?: number;
  handleRefetchDetails: (value: boolean) => void;
  onDelete?: () => void | Promise<void>;
}

const DetailsForm: React.FC<
  Pick<
    FormikProps<FormValues>,
    'errors' | 'touched' | 'isSubmitting' | 'values' | 'setFieldValue'
  > &
    Props
> = ({
  errors,
  touched,
  isSubmitting,
  onClose,
  values,
  setFieldValue,
  noticeId,
  handleRefetchDetails,
  onDelete,
  ...props
}) => {
  const t = usePrefix('Notices');

  const editorRef = useRef<Editor>(null);
  const editorInitValue = useMemo(() => values.content, []);

  const [, dispatch] = useApp();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const handleCancelUpload = () => {
    xhrRef.current?.abort();
    xhrRef.current = null;
    setUploadProgress(null);
  };

  const sendAttachment = (formData: FormData) => {
    const endpoint = `${url}/${values.type}/${noticeId}/attachment`;

    setUploadProgress(0);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    xhr.withCredentials = true;

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        // eslint-disable-next-line no-magic-numbers
        setUploadProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      xhrRef.current = null;
      setUploadProgress(null);
      // eslint-disable-next-line no-magic-numbers
      if (xhr.status >= 200 && xhr.status < 300) {
        handleRefetchDetails(true);
        dispatch({
          type: ActionTypes.SET_NOTIFICATION_CODE,
          payload: { type: notificationTypes.success },
        });
      } else {
        let code: string | undefined;
        try {
          code = (JSON.parse(xhr.responseText) as { code?: string })?.code;
        } catch (e) {
          code = undefined;
        }
        dispatch({
          type: ActionTypes.SET_NOTIFICATION_CODE,
          payload: { code, type: notificationTypes.error },
        });
      }
    });

    xhr.addEventListener('error', () => {
      xhrRef.current = null;
      setUploadProgress(null);
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { type: notificationTypes.error },
      });
    });

    xhr.addEventListener('abort', () => {
      xhrRef.current = null;
      setUploadProgress(null);
    });

    xhr.open('POST', endpoint);
    xhr.send(formData);
  };

  const handleFilePicker = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    sendAttachment(formData);
  };

  const handleEditorChange = useMemo(
    () =>
      debounce((value: string) => {
        setFieldValue('content', value);
      }, EDITOR_DEBOUNCE_TIME),
    [setFieldValue],
  );

  return (
    <Content {...props} autoComplete="off">
      <NoticeToolbarStyled role="toolbar">
        <DetailsTitle title={values.name || t('name')} empty={values.name.length < 1} />
        <NoticeActionsStyled role="group">
          {onDelete ? (
            <Button
              color="red"
              onClick={() => setIsDeleteOpen(true)}
              label={t('cta_delete_material')}
            />
          ) : null}
          <Button onClick={onClose} color="link">
            {t('dont_save_changes')}
          </Button>
          <Button label={t('save_changes')} type="submit" disabled={isSubmitting} />
        </NoticeActionsStyled>
      </NoticeToolbarStyled>
      <FormGroupStyled>
        <NoticeTypeSelect name="type" isDisabled={!!noticeId} />
        <LanguageSelect name="language" />
        <InputField name="name" error={touched.name && errors.name} label={t('name')} />
        <InputField name="title" error={touched.title && errors.title} label={t('title')} />
        {!!noticeId && (
          <FormField label={t('universally_available')} margin={'0'}>
            <ToggleSwitch checked={values.universally_available} name="universally_available" />
          </FormField>
        )}
      </FormGroupStyled>
      <FlexDiv justifyContent="flex-start" alignItems="flex-start" alignContent="flex-start">
        <FieldsContainer>
          <FlexDiv justifyContent="space-between" alignItems="flex-start">
            <EditorWrapper error={!!touched.content && !!errors.content}>
              <FieldTitle justifyContent="flex-start" alignItems="flex-start">
                <Label>{t(`editor_content_${values.type}`)}</Label>
                {!!touched.content && !!errors.content && (
                  <EditorMessage>- {t('editor_message')}</EditorMessage>
                )}
              </FieldTitle>
              <Editor
                ref={editorRef}
                initialValue={editorInitValue}
                apiKey="56vsxxenclq2luy8soh45et6js0pqavwt4gfscr2z1t3bw6v"
                init={{
                  images_upload_url: urlImages,
                  menubar: false,
                  plugins: [
                    'advlist',
                    'autoresize',
                    'autolink',
                    'lists',
                    'link',
                    'image',
                    'charmap',
                    'preview',
                    'anchor',
                    'searchreplace',
                    'visualblocks',
                    'code',
                    'fullscreen',
                    'insertdatetime',
                    'media',
                    'table',
                  ],
                  toolbar:
                    'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | link image',
                  link_assume_external_targets: 'https',
                }}
                onEditorChange={handleEditorChange}
              />
              {!!values.attachments?.length || uploadProgress !== null ? (
                <AttachmentsList
                  attachments={values.attachments ?? []}
                  uploadProgress={uploadProgress}
                  onCancelUpload={handleCancelUpload}
                  noticeId={noticeId}
                  onChange={(next) => setFieldValue('attachments', next)}
                  downloadAttachment={noticeAttachmentApi.downloadAttachment}
                  deleteAttachment={noticeAttachmentApi.deleteAttachment}
                  canEdit
                />
              ) : null}
              {!!noticeId && <FileUpload onChange={handleFilePicker} />}
              {!!noticeId && values.type === noticeTypes.intervention_plan && (
                <Rating noticeId={noticeId} inAdminForm />
              )}
            </EditorWrapper>
          </FlexDiv>
        </FieldsContainer>
      </FlexDiv>
      {isDeleteOpen && onDelete ? (
        <ConfirmModal
          title={t('delete_material_title')}
          text={t('delete_material_confirm', { name: values.name })}
          onCancel={() => setIsDeleteOpen(false)}
          onConfirm={async () => {
            setIsDeleteOpen(false);
            await onDelete();
          }}
        />
      ) : null}
    </Content>
  );
};

export default DetailsForm;
